import type { ReactElement, ReactNode } from 'react';
import { t } from '@fetmode/i18n';
import { resolveAppUrl, useCookieConsent } from '@fetmode/app-core';
import { Link } from 'react-router-dom';

import { ComplianceBadges } from '../../../features/compliance/index.js';
import './SiteFooter.css';

interface FooterLink {
  key: string;
  label: string;
  /** Internal route (React Router). */
  to?: string;
  /** External / app / mailto link. */
  href?: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
}

const studioUrl = resolveAppUrl('studio', import.meta.env);
const agencyUrl = resolveAppUrl('agency', import.meta.env);
const partnersUrl = resolveAppUrl('partners', import.meta.env);
const createUrl = resolveAppUrl('create', import.meta.env);

function buildColumns(): FooterColumn[] {
  return [
    {
      title: t('common.footer.exploreTitle'),
      links: [
        { key: 'cams', label: t('common.footer.cams'), to: '/cams' },
        { key: 'community', label: t('common.footer.community'), to: '/community' },
        { key: 'videos', label: t('common.footer.videos'), to: '/videos' },
        { key: 'wallet', label: t('common.footer.wallet'), to: '/wallet' },
      ],
    },
    {
      title: t('common.footer.creatorsTitle'),
      links: [
        { key: 'broadcast', label: t('common.footer.broadcast'), href: studioUrl },
        { key: 'agencies', label: t('common.footer.agencies'), href: agencyUrl },
        { key: 'affiliates', label: t('common.footer.affiliates'), href: partnersUrl },
        { key: 'creatorTools', label: t('common.footer.creatorTools'), href: createUrl },
      ],
    },
    {
      title: t('common.footer.companyTitle'),
      links: [
        { key: 'about', label: t('common.footer.about'), to: '/p/about' },
        { key: 'careers', label: t('common.footer.careers'), to: '/p/careers' },
        { key: 'blog', label: t('common.footer.blog'), to: '/p/blog' },
        { key: 'press', label: t('common.footer.press'), to: '/p/press' },
      ],
    },
    {
      title: t('common.footer.supportTitle'),
      links: [
        { key: 'help', label: t('common.footer.help'), to: '/p/help' },
        { key: 'safety', label: t('common.footer.safety'), to: '/p/safety' },
        { key: 'contact', label: t('common.footer.contact'), href: 'mailto:support@fetmode.com' },
        { key: 'feedback', label: t('common.footer.feedback'), to: '/p/feedback' },
        { key: 'reportContent', label: t('common.footer.reportContent'), href: 'mailto:abuse@fetmode.com' },
      ],
    },
    {
      title: t('common.footer.legalTitle'),
      links: [
        { key: 'terms', label: t('common.footer.terms'), to: '/p/terms' },
        { key: 'privacy', label: t('common.footer.privacy'), to: '/p/privacy' },
        { key: 'cookies', label: t('common.footer.cookies'), to: '/p/cookies' },
        { key: 'usc2257', label: t('common.footer.usc2257'), to: '/p/2257' },
        { key: 'dmca', label: t('common.footer.dmca'), to: '/p/dmca' },
        { key: 'contentRemoval', label: t('common.footer.contentRemoval'), to: '/p/content-removal' },
        { key: 'guidelines', label: t('common.footer.guidelines'), to: '/p/guidelines' },
      ],
    },
  ];
}

const SOCIAL_LINKS: SocialLink[] = [
  {
    key: 'x',
    label: t('common.footer.followX'),
    href: 'https://x.com/fetmode',
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
        <path
          fill="currentColor"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        />
      </svg>
    ),
  },
  {
    key: 'reddit',
    label: t('common.footer.followReddit'),
    href: 'https://www.reddit.com/r/fetmode',
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
        <path
          fill="currentColor"
          d="M22 12.06c0-1.2-.98-2.17-2.18-2.17-.58 0-1.1.23-1.5.6-1.47-1-3.46-1.63-5.65-1.72l.96-4.53 3.15.67a1.56 1.56 0 1 0 .17-.98l-3.52-.75a.49.49 0 0 0-.58.38l-1.07 5.04c-2.24.07-4.27.7-5.77 1.72a2.17 2.17 0 1 0-2.4 3.55 4 4 0 0 0-.05.63c0 3.2 3.73 5.8 8.33 5.8s8.33-2.6 8.33-5.8c0-.2-.02-.4-.05-.6.7-.39 1.15-1.12 1.15-1.95zM7.5 13.5a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0zm7.06 3.6c-.86.86-2.5.93-2.98.93-.48 0-2.12-.07-2.98-.93a.33.33 0 0 1 .47-.47c.54.54 1.7.73 2.51.73.81 0 1.97-.19 2.51-.73a.33.33 0 1 1 .47.47zM14.25 14.75a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"
        />
      </svg>
    ),
  },
  {
    key: 'instagram',
    label: t('common.footer.followInstagram'),
    href: 'https://www.instagram.com/fetmode',
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
        <path
          fill="currentColor"
          d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 3.29a6.55 6.55 0 1 0 0 13.1 6.55 6.55 0 0 0 0-13.1zm0 10.8a4.25 4.25 0 1 1 0-8.5 4.25 4.25 0 0 1 0 8.5zm6.81-11.05a1.53 1.53 0 1 0 0 3.06 1.53 1.53 0 0 0 0-3.06z"
        />
      </svg>
    ),
  },
  {
    key: 'telegram',
    label: t('common.footer.followTelegram'),
    href: 'https://t.me/fetmode',
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
        <path
          fill="currentColor"
          d="M21.94 4.66a1.2 1.2 0 0 0-1.62-1.15L2.9 10.27c-1.06.41-1.05 1-.19 1.27l4.5 1.4 1.74 5.34c.22.62.4.85.78.85.36 0 .55-.17.79-.4l2.16-2.1 4.5 3.32c.83.46 1.42.22 1.63-.77l2.94-13.8zM9.6 13.98l8.68-5.47c.38-.23.74-.11.45.15l-7.4 6.68-.29 3.06z"
        />
      </svg>
    ),
  },
];

export function SiteFooter({ omitWallet = false }: { omitWallet?: boolean }): ReactElement {
  const columns = buildColumns().map((column) => ({
    ...column,
    links: omitWallet ? column.links.filter((link) => link.to !== '/wallet') : column.links,
  }));
  const year = new Date().getFullYear();
  const { openCookiePreferences } = useCookieConsent();

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__top">
          <div className="site-footer__brand">
            <Link to="/" className="site-footer__brand-link">
              <img src="/brand/logo.png" alt="" width={32} height={32} aria-hidden />
              <span className="site-footer__brand-name">{t('common.app.name')}</span>
            </Link>
            <p className="site-footer__tagline">{t('common.app.tagline')}</p>
            <p className="site-footer__mission">{t('common.footer.mission')}</p>
            <nav className="site-footer__social" aria-label={t('common.footer.socialTitle')}>
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer__social-link"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </nav>
          </div>

          <div className="site-footer__columns">
            {columns.map((column) => (
              <nav key={column.title} className="site-footer__column" aria-label={column.title}>
                <h2 className="site-footer__column-title">{column.title}</h2>
                <ul className="site-footer__column-list">
                  {column.links.map((link) => (
                    <li key={link.key}>
                      {link.to ? (
                        <Link to={link.to} className="site-footer__link">
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="site-footer__link"
                          {...(link.href?.startsWith('http')
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <p className="site-footer__age-statement">{t('common.footer.ageStatement')}</p>

        <div className="site-footer__bottom">
          <div className="site-footer__badges">
            <ComplianceBadges />
          </div>
          <div className="site-footer__legal-row">
            <span className="site-footer__copyright">
              {t('common.footer.copyright').replace('{{year}}', String(year))}
            </span>
            <button
              type="button"
              className="site-footer__cookie-prefs"
              onClick={openCookiePreferences}
            >
              {t('common.footer.cookiePrefs')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
